import { PERMISSIONS } from './permissions';

export type AdminRoleRecord = {
  _id?: string;
  key: string;
  name: string;
  description: string;
  permissions: string[];
  status: 'active' | 'inactive';
};

export type PermissionCatalogItem = {
  key: string;
  label: string;
  description: string;
  module: string;
};

/** Default role-to-permission mapping from the RBAC specification (seeded in backend). */
export const SEEDED_ROLES: AdminRoleRecord[] = [
  {
    key: 'super-admin',
    name: 'Super Admin',
    description: 'Full platform access',
    permissions: [PERMISSIONS.ALL],
    status: 'active',
  },
  {
    key: 'admin',
    name: 'Admin',
    description: 'General administrator',
    permissions: [
      PERMISSIONS.USER_READ,
      PERMISSIONS.USER_WRITE,
      PERMISSIONS.TRANSACTION_READ,
      PERMISSIONS.TRANSACTION_WRITE,
      PERMISSIONS.TRANSACTION_EXECUTE,
      PERMISSIONS.TICKET_READ,
      PERMISSIONS.TICKET_WRITE,
      PERMISSIONS.KYC_REVIEW,
      PERMISSIONS.BANK_ACCOUNT_READ,
      PERMISSIONS.SETTINGS_READ,
      PERMISSIONS.SETTINGS_WRITE,
      PERMISSIONS.DASHBOARD_READ,
    ],
    status: 'active',
  },
  {
    key: 'customer-management',
    name: 'Customer Management',
    description: 'Manage customers and KYC',
    permissions: [PERMISSIONS.USER_READ, PERMISSIONS.USER_WRITE, PERMISSIONS.KYC_REVIEW],
    status: 'active',
  },
  {
    key: 'transaction-management',
    name: 'Transaction Management',
    description: 'Manage and execute transactions',
    permissions: [PERMISSIONS.TRANSACTION_READ, PERMISSIONS.TRANSACTION_WRITE, PERMISSIONS.TRANSACTION_EXECUTE],
    status: 'active',
  },
  {
    key: 'ticket-management',
    name: 'Ticket Management',
    description: 'Customer support tickets',
    permissions: [PERMISSIONS.TICKET_READ, PERMISSIONS.TICKET_WRITE],
    status: 'active',
  },
  {
    key: 'kyc-management',
    name: 'KYC Management',
    description: 'KYC review only',
    permissions: [PERMISSIONS.KYC_REVIEW],
    status: 'active',
  },
  {
    key: 'settings-management',
    name: 'Settings Management',
    description: 'Application settings',
    permissions: [PERMISSIONS.SETTINGS_READ, PERMISSIONS.SETTINGS_WRITE],
    status: 'active',
  },
  {
    key: 'reporting',
    name: 'Reporting',
    description: 'Dashboard and order reporting',
    permissions: [PERMISSIONS.DASHBOARD_READ],
    status: 'active',
  },
];

export const PERMISSION_CATALOG: PermissionCatalogItem[] = [
  {
    key: PERMISSIONS.ALL,
    label: 'Full admin access',
    description: 'Admin management, waiting list, and all modules',
    module: 'Admin',
  },
  {
    key: PERMISSIONS.USER_READ,
    label: 'Read users',
    description: 'Users list, details, referrals',
    module: 'Users',
  },
  {
    key: PERMISSIONS.USER_WRITE,
    label: 'Write users',
    description: 'Change user status and related updates',
    module: 'Users',
  },
  {
    key: PERMISSIONS.TRANSACTION_READ,
    label: 'Read transactions',
    description: 'Transactions, wallets, deposits, blockchain',
    module: 'Transactions',
  },
  {
    key: PERMISSIONS.TRANSACTION_WRITE,
    label: 'Write transactions',
    description: 'Transaction-related mutations',
    module: 'Transactions',
  },
  {
    key: PERMISSIONS.TRANSACTION_EXECUTE,
    label: 'Execute transactions',
    description: 'Execute-type transaction actions',
    module: 'Transactions',
  },
  {
    key: PERMISSIONS.TICKET_READ,
    label: 'Read tickets',
    description: 'Customer support ticket list',
    module: 'Support',
  },
  {
    key: PERMISSIONS.TICKET_WRITE,
    label: 'Write tickets',
    description: 'Update ticket status',
    module: 'Support',
  },
  {
    key: PERMISSIONS.KYC_REVIEW,
    label: 'KYC review',
    description: 'Review and update KYC submissions',
    module: 'KYC',
  },
  {
    key: PERMISSIONS.BANK_ACCOUNT_READ,
    label: 'Read bank accounts',
    description: 'View bank account registrations and details',
    module: 'Bank accounts',
  },
  {
    key: PERMISSIONS.SETTINGS_READ,
    label: 'Read settings',
    description: 'View application settings',
    module: 'Settings',
  },
  {
    key: PERMISSIONS.SETTINGS_WRITE,
    label: 'Write settings',
    description: 'Update application settings',
    module: 'Settings',
  },
  {
    key: PERMISSIONS.DASHBOARD_READ,
    label: 'Dashboard / reporting',
    description: 'Dashboard widgets and order reporting',
    module: 'Reporting',
  },
];

export const ADMIN_USER_STATUSES = ['active', 'inactive', 'suspended'] as const;

export const ROLE_STATUSES = ['active', 'inactive'] as const;
